import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { ButtonLink, ButtonLinkWithIcon, Icon } from '@blueprint/ui';
import { Heading2 } from '@blueprint/heading';
import {
    ArrowIconProps,
    ContentProps
} from './SearchSuggestionsTownsAndCities.types';

const SectionMarginTop = '64px';
const SectionMarginTopTablet = '72px';
const SectionMarginTopDesktop = '100px';

const topMargin = '1rem';
const iconWidthHeightMobile = '60px';
const iconWidthHeightDesktop = '80px';

export const SearchSuggestionsTownsAndCitiesWrapper = styled.div(
    ({ theme: { mq, spacing, colors } }) =>
        css`
            width: 100%;
            height: auto;
            background-color: ${colors['graphic-background']};
            margin: ${SectionMarginTop} 0;
            display: block;
            justify-content: space-between;
            position: relative;
            overflow: hidden;
            padding-left: ${spacing['16px']};

            ${mq.medium} {
                padding: 0;
                margin: ${SectionMarginTopTablet} 0;
            }

            ${mq.large} {
                overflow: visible;
                margin: ${SectionMarginTopDesktop} 0;
            }
        `
);

export const TownsAndCitiesHeadlineWrapper = styled.div(
    () =>
        css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            z-index: -1;
        `
);

export const Divider = styled.div(
    ({ theme: { mq, colors } }) =>
        css`
            display: none;

            ${mq.large} {
                display: block;
                width: 100%;
                height: 1px;
                margin: 58px 0;
                background-color: ${colors['neutral-3']};
            }
        `
);

export const StyledArrowsWrapperMobile = styled.div(
    ({ theme: { mq, spacing } }) =>
        css`
            display: block;
            position: absolute;
            right: 0;
            top: ${topMargin};
            margin-right: ${spacing['4px']};

            ${mq.medium} {
                display: none;
            }
        `
);

export const StyledArrowsWrapperTablet = styled.div(
    ({ theme: { mq, spacing } }) =>
        css`
            display: none;

            ${mq.medium} {
                display: block;
                position: absolute;
                right: 0;
                top: ${topMargin};
                margin-right: ${spacing['4px']};
            }

            ${mq.large} {
                display: none;
            }
        `
);

export const SectionWrapper = styled.div(
    ({ theme: { spacing, mq } }) =>
        css`
            width: 100%;
            margin-top: ${spacing['24px']};
            display: block;
            padding-right: ${spacing['16px']};

            ${mq.medium} {
                width: 50%;
                padding-right: 0;
            }

            ${mq.large} {
                display: flex;
                width: 100%;
            }
        `
);

export const Section = styled.div(
    () =>
        css`
            display: block;
            width: 100%;
        `
);

export const LinkWrapper = styled.div(
    () =>
        css`
            display: block;
        `
);

export const SectionIcon = styled.div(
    ({ theme: { spacing, mq } }) =>
        css`
            display: block;
            margin-right: ${spacing['16px']};
            max-width: ${iconWidthHeightMobile};
            max-height: ${iconWidthHeightMobile};

            ${mq.medium} {
                max-width: ${iconWidthHeightDesktop};
                max-height: ${iconWidthHeightDesktop};
            }
        `
);

export const IconAndHeadingWrapper = styled.div(
    () =>
        css`
            display: block;
        `
);

export const SectionHeading = styled.div(
    ({ theme: { spacing, mq } }) =>
        css`
            display: flex;
            width: 100%;
            padding-bottom: ${spacing['24px']};

            ${mq.medium} {
                padding-right: ${spacing['16px']};
            }

            ${mq.large} {
                padding-bottom: 0;
            }
        `
);

export const StyledButtonLinkWithIcon = styled(ButtonLinkWithIcon)(
    ({ theme: { spacing } }) =>
        css`
            text-decoration: none;
            text-align: left;
            padding: ${spacing['16px']} 0;
        `
);

export const StyledLink = styled(ButtonLink)(
    ({ theme: { spacing } }) =>
        css`
            padding: 0;
            margin-top: ${spacing['16px']};
            margin-bottom: ${spacing['16px']};
            text-align: left;
        `
);

export const getCarouselMargin = (carouselIndex: number) => {
    switch (carouselIndex) {
        case 0:
            return 0;
        case 1:
            return '-100%';
        case 2:
            return '-200%';
        case 3:
            return '-300%';
    }
};

export const ContentContainer = styled.div(
    ({ theme: { spacing } }) =>
        css`
            display: flex;
            flex-wrap: wrap;
            padding-top: ${spacing['64px']};
            position: relative;
        `
);

export const TabButtons = styled('div')(
    ({ theme: { mq } }) => css`
        display: flex;
        position: absolute;
        top: ${topMargin};
        left: 0;

        ${mq.medium} {
            left: 0;
        }

        ${mq.large} {
            top: -52px;
            left: 42.5%;
        }

        ${TabButton} {
            margin-right: 0.6875rem;

            &:last-of-type {
                margin-right: 0;
            }
        }
    `
);

export const TabButton = styled.a<{ isActive: boolean }>(
    ({ isActive, theme: { colors, borderRadius, type, focusRing } }) => css`
        text-decoration: none;
        background-color: ${colors['graphic-background']};
        display: block;
        padding: 0.75rem;
        cursor: pointer;
        font-family: inherit;
        font-weight: ${type['semiBold']};
        font-size: ${type.body};
        color: ${colors['neutral-6']};
        text-align: center;
        border: none;
        border-radius: ${borderRadius['300']};

        &:hover {
            text-decoration: underline;
        }

        &:focus {
            box-shadow: ${focusRing};
            outline: 0;
        }

        ${isActive &&
        `
            background-color: ${colors['graphic-background-contrast']};
            color: ${colors['text-contrast']};
        `};
    `
);

export const TabContent = styled.div<ContentProps>(
    ({ isVisible, carouselIndex, theme: { mq } }) =>
        css`
            display: flex;
            min-width: 400%;
            margin-left: ${getCarouselMargin(carouselIndex)};

            ${mq.medium} {
                min-width: 200%;
            }

            ${mq.large} {
                display: block;
                min-width: 100%;
                margin-left: 0;
            }

            ${isVisible === false &&
            `
                position: absolute;
                overflow: hidden;
                clip: rect(0 0 0 0);
                height: 1px;
                width: 1px;
                margin: -1px;
                padding: 0;
                border: 0;
            `}
        `
);

export const DesktopLink = styled(ButtonLink)(
    ({ theme: { mq, spacing } }) =>
        css`
            display: none;

            ${mq.medium} {
                display: block;
                position: absolute;
                top: -${spacing['32px']};
                right: 0;
                margin: 0;
                min-width: 230px;
                padding-right: 0;
            }
            ${mq.large} {
                top: -52px;
            }
        `
);

export const MobileLink = styled(ButtonLink)(
    ({ theme: { mq } }) =>
        css`
            display: block;
            margin: auto;

            ${mq.medium} {
                display: none;
            }
        `
);

export const StyledHeading = styled(Heading2)(
    ({ theme: { mq, type, spacing } }) =>
        css`
            padding-right: ${spacing['16px']};
            width: 100%;

            ${mq.large} {
                padding-bottom: ${spacing['16px']};
                font-size: ${type[4]};
            }
        `
);

export const StyledArrowIconRight = styled(Icon)<ArrowIconProps>(
    ({ theme: { mq } }) =>
        css`
            ${mq.large} {
                display: none;
            }
        `
);

export const StyledArrowIconLeft = styled(Icon)<ArrowIconProps>(
    ({ theme: { mq } }) =>
        css`
            ${mq.large} {
                display: none;
            }
        `
);

export const StyledImage = styled.img(
    ({ theme: { mq } }) =>
        css`
            width: ${iconWidthHeightMobile};

            ${mq.medium} {
                width: ${iconWidthHeightDesktop};
            }
        `
);
